import { generatePath, useLocation, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import I18n from 'I18n';
import styled from 'academy-common-lib/utils/Styling';
import { OZ_DARK } from 'HubStyleTokens/colors';
import ACMissingContentError from 'academy-common-lib/components/error/ACMissingContentError';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getFullUrl } from 'hubspot-url-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIToolBar from 'UIComponents/nav/UIToolBar';
import UIToolBarGroup from 'UIComponents/nav/UIToolBarGroup';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIEditorPage from 'UIComponents/page/UIEditorPage';
import { useGetToolRecommendations } from '../queries/softwareDiscoveryHooks';
import { useRequestLearnWhileDoing } from '../hooks';
import LearnByDoingDraggableContainer from './LearnByDoing/DraggableContainer/LearnByDoingDraggableContainer';
import { useLearnByDoing } from '../hooks';
import { useHomepageUrl } from '../../navigation/navigationQueries';
import UsageTracker from '../../../utils/UsageTracker';
import { NavMarker } from 'react-rhumb';
import { useShort } from '../../shortContent/shortContentQueries';
import useNavigate from '../../../hooks/useNavigate';
import { SHORT_CONTENT_URL_FRAGMENT } from '../../../constants/Navigation';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledEditorPage = styled(UIEditorPage).withConfig({
  displayName: "SoftwareDiscoveryIframe__StyledEditorPage",
  componentId: "sc-3p3s5b-0"
})(["position:absolute;top:0;bottom:0;left:0;right:0;z-index:5;"]);
const UIToolbarThunderdome = styled(UIToolBar).withConfig({
  displayName: "SoftwareDiscoveryIframe__UIToolbarThunderdome",
  componentId: "sc-3p3s5b-1"
})(["background:", " !important;"], OZ_DARK);
const Title = styled.div.withConfig({
  displayName: "SoftwareDiscoveryIframe__Title",
  componentId: "sc-3p3s5b-2"
})(["font-size:16px;"]);
const FullscreenIframe = styled.iframe.withConfig({
  displayName: "SoftwareDiscoveryIframe__FullscreenIframe",
  componentId: "sc-3p3s5b-3"
})(["width:100%;height:100%;border:0;overflow:scroll;position:absolute;"]);
const SoftwareDiscoveryIframe = ({
  useCases,
  toolSlugProp = ''
}) => {
  const {
    homepage
  } = useHomepageUrl();
  const homePageUrl = `${getFullUrl('app')}${homepage}`;
  const {
    shouldRenderLearnByDoingScreen
  } = useLearnByDoing();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    toolSlug = ''
  } = useParams();
  const {
    search
  } = location;
  const params = new URLSearchParams(search);
  const experimentUseCases = params.get('useCases') || useCases || '';
  const otgId = params.get('otgId');
  const isLearnWhileDoing = !!JSON.parse(params.get('isLearnWhileDoing') || 'false');
  const {
    handleEnableLearnWhileDoing,
    handleDisableLearnWhileDoing,
    isLearnWhileDoingEnabled
  } = useRequestLearnWhileDoing();
  useEffect(() => {
    if (isLearnWhileDoing) {
      handleEnableLearnWhileDoing();
    }
  }, [isLearnWhileDoing, handleEnableLearnWhileDoing]);
  const {
    short
  } = useShort(Number(otgId));
  const {
    hubId: portalId,
    contentHublet: hublet
  } = short || {};
  const toolRecommendations = useGetToolRecommendations([experimentUseCases]);
  const resourceIndex = toolRecommendations && toolRecommendations.toolSlug.indexOf(toolSlug || toolSlugProp);
  const hasToolRecommendations = !!(toolRecommendations && resourceIndex !== undefined && resourceIndex > -1);
  const toolUrl = hasToolRecommendations && toolRecommendations.toolCardLinks[resourceIndex] || homePageUrl;
  const toolLabelTitle = hasToolRecommendations && toolRecommendations.toolLabelTitles[resourceIndex];
  const toolLabel = toolLabelTitle || (homePageUrl ? 'learningCenter.toolRecommendations.learnByDoingEditorHeader' : 'learningCenter.errors.deprecated.heading');
  const toolLoading = toolRecommendations && toolRecommendations.toolLoading;
  useEffect(() => {
    const hideZorse = document.createElement('style');
    hideZorse.innerHTML = `#isc-zorse-widget, #chat-widget {
        display:none; }`;
    document.getElementsByTagName('head')[0].appendChild(hideZorse);
    return function cleanup() {
      handleDisableLearnWhileDoing();
      hideZorse.remove();
    };
  }, [handleDisableLearnWhileDoing]);
  const closeIframe = () => {
    window.close();
  };
  const handleExitIframe = () => {
    if (isLearnWhileDoingEnabled) {
      const otgPath = generatePath(SHORT_CONTENT_URL_FRAGMENT, {
        shortId: otgId
      });
      handleDisableLearnWhileDoing();
      navigate(otgPath);
    } else {
      closeIframe();
    }
    UsageTracker.track('contentInteraction', {
      action: 'learn-by-doing-back-to-academy-clicked',
      shortId: String(otgId)
    });
  };
  return /*#__PURE__*/_jsx(StyledEditorPage, {
    hasScrolling: true,
    headerComponent: /*#__PURE__*/_jsxs(UIToolbarThunderdome, {
      use: "dark",
      children: [/*#__PURE__*/_jsx(UIToolBarGroup, {
        children: /*#__PURE__*/_jsx(Title, {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            elements: {
              Message: FormattedMessage
            },
            message: "learningCenter.toolRecommendations.iframeHeader_jsx",
            options: {
              toolLabel
            }
          })
        })
      }), /*#__PURE__*/_jsx(UIToolBarGroup, {
        style: {
          justifyContent: 'right'
        },
        children: /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "back-to-academy-button-cta",
          onClick: handleExitIframe,
          use: "tertiary-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.toolRecommendations.backToAcademy"
          })
        })
      })]
    }),
    mainSectionFlush: true,
    use: "dark",
    children: toolLoading ? /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true,
      minHeight: 200
    }) : shouldRenderLearnByDoingScreen ? /*#__PURE__*/_jsx(DndProvider, {
      backend: HTML5Backend,
      children: /*#__PURE__*/_jsx(LearnByDoingDraggableContainer, {
        hublet: hublet,
        otgId: Number(otgId),
        portalId: portalId,
        children: /*#__PURE__*/_jsx(NavMarker, {
          name: "SOFTWARE_DISCOVERY_LOADED",
          children: /*#__PURE__*/_jsx(FullscreenIframe, {
            "data-test-id": "software-discovery-iframe",
            src: toolUrl,
            title: I18n.text('learningCenter.toolRecommendations.AcademyLearnByDoing')
          })
        })
      })
    }) : toolLabelTitle ? /*#__PURE__*/_jsx(NavMarker, {
      name: "SOFTWARE_DISCOVERY_LOADED",
      children: /*#__PURE__*/_jsx(FullscreenIframe, {
        "data-test-id": "software-discovery-iframe",
        src: toolUrl,
        title: I18n.text(toolLabelTitle)
      })
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "SOFTWARE_DISCOVERY_ERROR",
      children: /*#__PURE__*/_jsx(ACMissingContentError, {})
    })
  });
};
export default SoftwareDiscoveryIframe;